<template>
  <div class="equipment-session stack tight">
    <UiPanel>
      <div class="header">
        <div>
          <h3 class="title">Equipment</h3>
          <div class="subtitle text-muted">Your current loadout.</div>
        </div>
        <div class="header-actions">
          <UiChip variant="muted">
            Slots <span class="stat-mono num">{{ formatNumber(equippedCount) }}</span>
            <span class="text-muted">/</span>
            <span class="stat-mono num">{{ formatNumber(allSlots.length) }}</span>
          </UiChip>
          <UiButton
            size="sm"
            variant="secondary"
            :disabled="sessionStore.loading || equippedCount === 0"
            @click="handleUnequipAll"
          >
            Unequip all
          </UiButton>
        </div>
      </div>
    </UiPanel>

    <UiPanel v-if="nonProficientArmor || nonProficientWeapon" class="proficiency-banner">
      <div v-if="nonProficientArmor" class="warning-text">Not proficient: Initiative at disadvantage</div>
      <div v-if="nonProficientWeapon" class="warning-text">Not proficient: Attacks at disadvantage</div>
    </UiPanel>

    <UiPanel class="presets-panel">
      <UiSectionHeader>
        <template #title>Presets</template>
        <template #actions>
          <label v-if="presets.length > 0" class="preset-select">
            <span class="sr-only">Select preset</span>
            <select v-model="selectedPresetId" class="ui-select">
              <option v-for="preset in presets" :key="preset.id" :value="preset.id">
                Preset {{ formatNumber(preset.slotIndex + 1) }} — {{ preset.name }}
              </option>
            </select>
          </label>
        </template>
      </UiSectionHeader>

      <UiCallout v-if="presetsError" variant="warning">
        {{ presetsError }}
      </UiCallout>
      <div v-else-if="presetsLoading" class="text-muted">Loading presets...</div>
      <div v-else-if="presets.length === 0" class="text-muted">No presets yet.</div>
      <div v-else>
        <div v-if="selectedPreset" class="preset-card" :class="{ active: selectedPreset.isActive, busy: isBusy(selectedPreset.id) }">
          <div class="preset-header">
            <div class="preset-title">
              <span class="preset-index">Preset {{ formatNumber(selectedPreset.slotIndex + 1) }}</span>
              <div v-if="editingPresetId !== selectedPreset.id" class="preset-name">
                {{ selectedPreset.name }}
              </div>
              <input
                v-else
                v-model="renameDraft"
                class="ui-input preset-input"
                maxlength="48"
                placeholder="Preset name"
              />
            </div>
            <div class="preset-chips">
              <UiChip v-if="selectedPreset.isActive" variant="active">Active</UiChip>
              <UiChip v-if="selectedPreset.isEmpty" variant="muted">Empty</UiChip>
              <UiChip v-if="selectedPreset.isActive && activePresetDiff?.outOfSync" variant="warning">Out of sync</UiChip>
            </div>
          </div>

          <div class="preset-meta text-muted">
            <span>{{ presetSummary(selectedPreset) }}</span>
          </div>

          <div v-if="editingPresetId === selectedPreset.id" class="preset-actions">
            <UiButton size="sm" variant="secondary" :disabled="isBusy(selectedPreset.id)" @click="cancelRename">
              Cancel
            </UiButton>
            <UiButton size="sm" :disabled="isBusy(selectedPreset.id)" @click="commitRename(selectedPreset)">
              Save name
            </UiButton>
          </div>
          <div v-else class="preset-actions">
            <UiButton size="sm" variant="secondary" :disabled="isBusy(selectedPreset.id)" @click="handleSave(selectedPreset)">
              Save current
            </UiButton>
            <UiButton size="sm" :disabled="isBusy(selectedPreset.id)" @click="handleApply(selectedPreset)">
              Apply
            </UiButton>
            <UiButton size="sm" variant="secondary" :disabled="isBusy(selectedPreset.id)" @click="startRename(selectedPreset)">
              Rename
            </UiButton>
            <UiButton size="sm" variant="danger" :disabled="isBusy(selectedPreset.id)" @click="handleClear(selectedPreset)">
              Clear
            </UiButton>
          </div>

          <div v-if="isBusy(selectedPreset.id)" class="preset-busy text-muted">Working...</div>
        </div>
      </div>
    </UiPanel>

    <div class="grid">
      <UiPanel
        v-for="slot in allSlots"
        :key="slot"
        class="slot"
        :class="{ empty: !equipmentResolved[slot], 'non-proficient': isNonProficient(equipmentResolved[slot]) }"
      >
        <div class="slot-label">
          <span class="slot-icon" aria-hidden="true">{{ ICONS.equipmentSlots[slot] }}</span>
          <span>{{ slotLabels[slot] }}</span>
        </div>

        <div v-if="equipmentResolved[slot]" class="slot-body">
          <UiTooltip :hoverDelay="360">
            <div class="item">
              <div class="item-icon" aria-hidden="true">{{ itemIconForType(equipmentResolved[slot]!.item?.type) }}</div>
              <div style="min-width: 0;">
                <div class="item-name" :class="`rarity-${equipmentResolved[slot]!.item?.rarity || 'common'}`">
                  {{ equipmentResolved[slot]!.item?.name || equipmentResolved[slot]!.itemId }}
                </div>
                <div class="item-sub text-muted">
                  <span class="mark">{{ markForEntry(equipmentResolved[slot]!) }}</span>
                  <span class="dot">•</span>
                  <span>{{ equipmentResolved[slot]!.item?.type || "Unknown" }}</span>
                  <span v-if="isNonProficient(equipmentResolved[slot])" class="dot">•</span>
                  <span v-if="isNonProficient(equipmentResolved[slot])" class="warning-text">Non-proficient</span>
                </div>
                <div v-if="equipmentResolved[slot]!.item?.armorCategory" class="item-sub text-muted">
                  Category {{ formatArmorCategory(equipmentResolved[slot]!.item!.armorCategory) }}
                  <span class="dot">•</span>
                  Tier {{ equipmentResolved[slot]!.item!.armorTier ?? "?" }}
                </div>
              </div>
            </div>

            <template #content>
              <div class="tooltip stack tight">
                <div class="tooltip-title">
                  <span class="tooltip-icon">{{ itemIconForType(equipmentResolved[slot]!.item?.type) }}</span>
	                  <div style="flex: 1; min-width: 0;">
	                    <div class="tooltip-name">{{ equipmentResolved[slot]!.item?.name || equipmentResolved[slot]!.itemId }}</div>
	                    <div class="text-muted tooltip-sub">
	                      {{ slotLabels[slot] }} • {{ tooltipMarkForEntry(equipmentResolved[slot]!) }}
	                    </div>
	                  </div>
	                </div>

                <div v-if="equipmentResolved[slot]!.enchantment" class="text-muted">
                  {{ ICONS.skills.enchanting }} {{ enchantEffectText(equipmentResolved[slot]!.enchantment!) }}
                </div>
                <div v-for="line in getArmorIdentityLines(equipmentResolved[slot]!.item)" :key="line" class="text-muted">
                  {{ line }}
                </div>
                <div v-if="getCombatStatsText(equipmentResolved[slot]!.item)" class="text-muted">
                  {{ getCombatStatsText(equipmentResolved[slot]!.item) }}
                </div>
                <div v-if="getProficiencyWarning(equipmentResolved[slot]!.item)" class="text-muted warning-text">
                  {{ getProficiencyWarning(equipmentResolved[slot]!.item) }}
                </div>
                <div v-if="equipmentResolved[slot]!.item?.description" class="text-muted">
                  {{ equipmentResolved[slot]!.item?.description }}
                </div>
              </div>
            </template>
          </UiTooltip>

          <UiButton size="sm" variant="secondary" @click="emit('unequip', slot)">
            Unequip
          </UiButton>
        </div>

        <div v-else class="slot-empty text-muted">
          Empty
        </div>
      </UiPanel>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import type { Enchantment, EquipmentSlot, InventoryEntry, Item, LoadoutPreset } from "@idle-rpg/shared";
import { getItemMarkDisplay, getItemMarkTooltipDisplay } from "../../../lib/itemMarks";
import { useItemMarkMode } from "../../../composables/useItemMarkMode";
import { itemIconForType } from "../../../lib/itemIcons";
import { ICONS } from "../../../lib/icons";
import { formatNumber } from "../../../lib/format";
import { useConfirm } from "../../../composables/useConfirm";
import { useToast } from "../../../composables/useToast";
import { useSessionStore } from "../../../store/session";
import { computePresetDiff } from "../application/presetDiff";
import { useLoadoutPresetsState } from "../application/useLoadoutPresetsState";
import UiButton from "../../../components/ui/UiButton.vue";
import UiCallout from "../../../components/ui/UiCallout.vue";
import UiChip from "../../../components/ui/UiChip.vue";
import UiPanel from "../../../components/ui/UiPanel.vue";
import UiSectionHeader from "../../../components/ui/UiSectionHeader.vue";
import UiTooltip from "../../../components/ui/UiTooltip.vue";

const props = defineProps<{
  equipment: Record<EquipmentSlot, InventoryEntry | null>;
  armorProficiencies?: string[];
  weaponProficiencies?: string[];
}>();

const emit = defineEmits<{
  (e: "unequip", slot: EquipmentSlot): void;
}>();

const { mode: itemMarkMode } = useItemMarkMode();
const sessionStore = useSessionStore();
const { confirm } = useConfirm();
const { addToast } = useToast();
const loadoutPresets = useLoadoutPresetsState();
const editingPresetId = ref<string | null>(null);
const renameDraft = ref("");

const slotLabels: Record<EquipmentSlot, string> = {
  head: "Head",
  neck: "Neck",
  chest: "Chest",
  back: "Back",
  main_hand: "Main Hand",
  off_hand: "Off Hand",
  hands: "Hands",
  ring: "Ring",
  legs: "Legs",
  feet: "Feet"
};

const allSlots: EquipmentSlot[] = ["head", "neck", "chest", "back", "main_hand", "off_hand", "hands", "ring", "legs", "feet"];

const equipmentResolved = computed(() => props.equipment);

const equippedCount = computed(
  () => allSlots.reduce((count, slot) => (equipmentResolved.value[slot] ? count + 1 : count), 0)
);

const presetsState = computed(() => loadoutPresets.state.value);
const presets = computed(() => presetsState.value?.presets ?? []);
const presetsLoading = computed(() => loadoutPresets.loading.value);
const presetsError = computed(() => loadoutPresets.error.value);
const selectedPresetId = ref<string | null>(null);

const activePreset = computed(() => {
  const state = presetsState.value;
  if (!state) return null;
  if (state.activePresetId) {
    return state.presets.find((preset) => preset.id === state.activePresetId) ?? null;
  }
  return state.presets.find((preset) => preset.isActive) ?? null;
});

const activePresetDiff = computed(() => {
  if (!activePreset.value) return null;
  return computePresetDiff({
    equipment: props.equipment,
    presetSlots: activePreset.value.slots
  });
});

const selectedPreset = computed(() => {
  if (selectedPresetId.value) {
    return presets.value.find((preset) => preset.id === selectedPresetId.value) ?? null;
  }
  return activePreset.value ?? presets.value[0] ?? null;
});

const nonProficientArmor = computed(() => {
  const profs = props.armorProficiencies ?? [];
  if (!profs.length) return false;
  for (const slot of allSlots) {
    const item = equipmentResolved.value[slot]?.item;
    if (!item?.armorCategory) continue;
    if (!profs.includes(item.armorCategory)) return true;
  }
  return false;
});

const nonProficientWeapon = computed(() => {
  const profs = props.weaponProficiencies ?? [];
  if (!profs.length) return false;
  for (const slot of allSlots) {
    const item = equipmentResolved.value[slot]?.item;
    if (!item || item.type !== "weapon") continue;
    const style = weaponStyleFromTags(item.tags);
    if (!profs.includes(style)) return true;
  }
  return false;
});

	function markForEntry(entry: InventoryEntry): string {
	  return getItemMarkDisplay(itemMarkMode.value, entry.itemId, entry.variantKey ?? null, entry.enchantment?.id ?? null);
	}

	function tooltipMarkForEntry(entry: InventoryEntry): string {
	  return getItemMarkTooltipDisplay(itemMarkMode.value, entry.itemId, entry.variantKey ?? null, entry.enchantment?.id ?? null);
	}

function getCombatStatsText(item?: Item): string {
  if (!item) return "";
  const stats: string[] = [];
  if (item.armorClass && item.armorClass > 0) stats.push(`AC +${item.armorClass}`);
  if (item.attackBonus !== undefined || item.damageDice) {
    const parts: string[] = [];
    if (item.attackBonus && item.attackBonus !== 0) parts.push(`${item.attackBonus >= 0 ? "+" : ""}${item.attackBonus} Attack`);
    if (item.damageDice) {
      const damageStr =
        item.damageBonus && item.damageBonus !== 0
          ? `${item.damageDice}${item.damageBonus >= 0 ? "+" : ""}${item.damageBonus}`
          : item.damageDice;
      parts.push(damageStr);
    }
    if (parts.length > 0) stats.push(parts.join(", "));
  }
  return stats.join(" • ");
}

function getArmorIdentityLines(item?: Item): string[] {
  if (!item?.armorCategory) return [];
  const lines: string[] = [];
  const category = formatArmorCategory(item.armorCategory);
  const tier = item.armorTier ? `Tier ${item.armorTier}` : "Tier ?";
  lines.push(`Category: ${category} • ${tier}`);
  if (item.carryCapacitySlotsMod && item.carryCapacitySlotsMod !== 0) {
    const sign = item.carryCapacitySlotsMod > 0 ? "+" : "";
    lines.push(`Carry Capacity ${sign}${item.carryCapacitySlotsMod}`);
  }
  if (item.maxManaMod && item.maxManaMod !== 0) {
    const sign = item.maxManaMod > 0 ? "+" : "";
    lines.push(`Max Mana ${sign}${item.maxManaMod}`);
  }
  if (item.manaRegenBonusPerTick && item.manaRegenBonusPerTick !== 0) {
    const sign = item.manaRegenBonusPerTick > 0 ? "+" : "";
    lines.push(`Mana Regen ${sign}${item.manaRegenBonusPerTick}/tick`);
  }
  return lines;
}

function weaponStyleFromTags(tags?: string[]): string {
  if (!tags || tags.length === 0) return "melee";
  if (tags.includes("ranged")) return "ranged";
  if (tags.includes("magic")) return "magic";
  return "melee";
}

function formatArmorCategory(category: string): string {
  if (!category) return "Armor";
  return category.slice(0, 1).toUpperCase() + category.slice(1);
}

function isNonProficient(entry: InventoryEntry | null | undefined): boolean {
  const item = entry?.item;
  if (!item) return false;
  if (item.armorCategory) {
    const profs = props.armorProficiencies ?? [];
    return profs.length > 0 && !profs.includes(item.armorCategory);
  }
  if (item.type === "weapon") {
    const style = weaponStyleFromTags(item.tags);
    const profs = props.weaponProficiencies ?? [];
    return profs.length > 0 && !profs.includes(style);
  }
  return false;
}

watch(
  () => [sessionStore.token, sessionStore.character?.id] as const,
  ([token, characterId], prev) => {
    const [prevToken, prevCharacterId] = Array.isArray(prev) ? prev : [];
    if (!token || !characterId) {
      loadoutPresets.clearState();
      editingPresetId.value = null;
      renameDraft.value = "";
      return;
    }
    const force = token !== prevToken || characterId !== prevCharacterId;
    void loadoutPresets.refresh({ force });
  },
  { immediate: true }
);

watch(
  () => [presets.value, presetsState.value?.activePresetId] as const,
  ([nextPresets, activeId]) => {
    if (!nextPresets.length) {
      selectedPresetId.value = null;
      return;
    }
    const currentId = selectedPresetId.value;
    const stillExists = currentId ? nextPresets.some((preset) => preset.id === currentId) : false;
    if (stillExists) return;
    selectedPresetId.value = activeId ?? nextPresets[0].id;
  },
  { immediate: true }
);

watch(
  () => selectedPresetId.value,
  (nextId) => {
    if (editingPresetId.value && editingPresetId.value !== nextId) {
      cancelRename();
    }
  }
);

function isBusy(presetId: string) {
  if (sessionStore.loading) return true;
  if (editingPresetId.value && editingPresetId.value !== presetId) return true;
  if (loadoutPresets.loading.value) return true;
  return loadoutPresets.busyPresetId.value === presetId;
}

function presetSummary(preset: LoadoutPreset): string {
  const filled = preset.slots.filter((slot) => slot.itemId).length;
  if (filled === 0) return "Empty preset";
  return `${formatNumber(filled)} / ${formatNumber(allSlots.length)} slots saved`;
}

function startRename(preset: LoadoutPreset) {
  editingPresetId.value = preset.id;
  renameDraft.value = preset.name;
}

function cancelRename() {
  editingPresetId.value = null;
  renameDraft.value = "";
}

async function commitRename(preset: LoadoutPreset) {
  const name = renameDraft.value.trim();
  if (!name) {
    addToast("Name is required", "warning");
    return;
  }
  const res = await loadoutPresets.rename(preset.id, name);
  if (!res) {
    addToast(presetsError.value || "Failed to rename preset", "warning");
    return;
  }
  addToast(`Renamed to ${res.preset.name}`, "success");
  cancelRename();
}

async function handleSave(preset: LoadoutPreset) {
  const res = await loadoutPresets.save(preset.id);
  if (!res) {
    addToast(presetsError.value || "Failed to save preset", "warning");
    return;
  }
  addToast(`Saved ${res.preset.name}`, "success");
}

async function handleApply(preset: LoadoutPreset) {
  const diff = computePresetDiff({ equipment: props.equipment, presetSlots: preset.slots });
  const equipCount = diff.changes.filter((change) => change.action === "equip").length;
  const unequipCount = diff.changes.filter((change) => change.action === "unequip").length;
  const totalChanges = diff.changes.length;
  const summaryParts = [];
  if (equipCount > 0) summaryParts.push(`equip ${formatNumber(equipCount)}`);
  if (unequipCount > 0) summaryParts.push(`unequip ${formatNumber(unequipCount)}`);
  const summary = totalChanges ? `Will ${summaryParts.join(" and ")}.` : "No slot changes detected.";
  const ok = await confirm({
    variant: unequipCount > 0 ? "danger" : "default",
    title: `Apply ${preset.name}?`,
    subtitle: totalChanges
      ? `${formatNumber(totalChanges)} slot change${totalChanges === 1 ? "" : "s"}`
      : "Preset already matches your loadout.",
    body: summary,
    confirmLabel: "Apply preset",
    cancelLabel: "Cancel",
    closeOnScrim: true
  });
  if (!ok) return;
  const res = await loadoutPresets.apply(preset.id);
  if (!res) {
    addToast(presetsError.value || "Failed to apply preset", "warning");
    return;
  }
  const applySummaryParts = [
    `Applied ${formatNumber(res.summary.appliedSlots)} slot${res.summary.appliedSlots === 1 ? "" : "s"}`
  ];
  if (res.summary.missingSlots > 0) {
    applySummaryParts.push(`${formatNumber(res.summary.missingSlots)} missing`);
  }
  if (res.summary.blockedSlots > 0) {
    applySummaryParts.push(`${formatNumber(res.summary.blockedSlots)} blocked`);
  }
  addToast(
    applySummaryParts.join(" / "),
    res.summary.missingSlots || res.summary.blockedSlots ? "warning" : "success"
  );
}

async function handleClear(preset: LoadoutPreset) {
  const ok = await confirm({
    variant: "danger",
    title: `Clear ${preset.name}?`,
    subtitle: "This removes all saved slot choices.",
    confirmLabel: "Clear preset",
    cancelLabel: "Cancel",
    closeOnScrim: true
  });
  if (!ok) return;
  const res = await loadoutPresets.clear(preset.id);
  if (!res) {
    addToast(presetsError.value || "Failed to clear preset", "warning");
    return;
  }
  addToast("Preset cleared", "success");
}

async function handleUnequipAll() {
  if (equippedCount.value === 0) return;
  const ok = await confirm({
    variant: "danger",
    title: "Unequip all items?",
    subtitle: "All equipped gear will move back to your inventory.",
    confirmLabel: "Unequip all",
    cancelLabel: "Cancel",
    closeOnScrim: true
  });
  if (!ok) return;
  const slotsToUnequip = allSlots.filter((slot) => equipmentResolved.value[slot]);
  let succeeded = 0;
  let failed = 0;
  for (const slot of slotsToUnequip) {
    const result = await sessionStore.unequipItem(slot);
    if (result) {
      succeeded += 1;
    } else {
      failed += 1;
    }
  }
  if (succeeded === 0 && failed > 0) {
    addToast(sessionStore.error || "Unable to unequip items", "warning");
    return;
  }
  const parts = [`Unequipped ${formatNumber(succeeded)} slot${succeeded === 1 ? "" : "s"}`];
  if (failed > 0) parts.push(`${formatNumber(failed)} failed`);
  addToast(parts.join(" / "), failed > 0 ? "warning" : "success");
}

function getProficiencyWarning(item?: Item): string {
  if (!item) return "";
  if (item.armorCategory) {
    const profs = props.armorProficiencies ?? [];
    if (profs.length > 0 && !profs.includes(item.armorCategory)) {
      return "Non-proficient armor: initiative rolls at disadvantage.";
    }
  }
  if (item.type === "weapon") {
    const style = weaponStyleFromTags(item.tags);
    const profs = props.weaponProficiencies ?? [];
    if (profs.length > 0 && !profs.includes(style)) {
      return "Non-proficient weapon: attacks roll at disadvantage.";
    }
  }
  return "";
}

function enchantEffectText(enchant: Enchantment): string {
  const sign = enchant.amount >= 0 ? `+${enchant.amount}` : `${enchant.amount}`;
  if (enchant.effectType === "attribute") return `${sign} ${String(enchant.target || "").toUpperCase()}`.trim();
  if (enchant.effectType === "skill") {
    const skill = String(enchant.target || "");
    const label = skill ? skill.slice(0, 1).toUpperCase() + skill.slice(1) : "Skill";
    return `${sign} ${label}`;
  }
  if (enchant.effectType === "ward") {
    const ward = enchant.wardType === "mana" ? "Mana" : "HP";
    return `Ward ${sign} ${ward}`;
  }
  return enchant.name;
}
</script>

<style scoped>
.equipment-session {
  min-width: 0;
}

.header {
  display: flex;
  justify-content: space-between;
  gap: var(--space-md);
  align-items: flex-start;
}

.header-actions {
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.title {
  margin: 0;
  font-family: var(--font-display);
  letter-spacing: 0.02em;
}

.subtitle {
  font-size: 0.9rem;
  margin-top: 0.15rem;
}

.presets-panel {
  display: grid;
  gap: var(--space-sm);
}

.preset-select {
  display: inline-flex;
  min-width: 200px;
}

.preset-select .ui-select {
  width: 100%;
}

.preset-card {
  display: grid;
  gap: 0.5rem;
  padding: var(--space-sm);
  border-radius: var(--radius-2);
  border: 1px solid var(--glass-border-0);
  background: var(--glass-0);
  min-width: 0;
}

.preset-card.active {
  border-color: color-mix(in srgb, var(--state-active) 55%, var(--glass-border-0));
  box-shadow: 0 0 0 1px color-mix(in srgb, var(--state-active) 28%, transparent);
}

.preset-card.busy {
  opacity: 0.8;
}

.preset-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-sm);
}

.preset-title {
  min-width: 0;
  display: grid;
  gap: 0.2rem;
}

.preset-index {
  font-size: 0.7rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--color-subtle);
}

.preset-name {
  font-weight: 700;
  letter-spacing: 0.02em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.preset-input {
  width: 100%;
}

.preset-chips {
  display: inline-flex;
  gap: 0.4rem;
  flex-wrap: wrap;
}

.preset-meta {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.5rem;
  font-size: 0.82rem;
  flex-wrap: wrap;
}

.preset-actions {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.preset-busy {
  font-size: 0.8rem;
}

.grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: var(--space-md);
}

.slot {
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);
  min-height: 120px;
}

.slot.empty {
  opacity: 0.88;
}

.slot.non-proficient {
  border-color: color-mix(in srgb, var(--color-warning) 55%, var(--border-1));
  box-shadow: 0 0 0 1px color-mix(in srgb, var(--color-warning) 35%, transparent);
}

.slot-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--color-subtle);
  display: flex;
  align-items: center;
  gap: 0.35rem;
}

.slot-icon {
  font-size: 0.95rem;
}

.slot-body {
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);
  flex: 1;
}

.slot-empty {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius-md);
  border: 1px dashed color-mix(in srgb, var(--border-1) 55%, transparent);
  background: var(--glass-0);
  padding: var(--space-md);
}

.item {
  display: flex;
  gap: var(--space-sm);
  align-items: flex-start;
}

.item-icon {
  width: 40px;
  height: 40px;
  border-radius: var(--radius-sm);
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--glass-1);
  border: 1px solid var(--glass-border-0);
  font-size: 1.15rem;
}

.item-name {
  font-weight: 750;
  letter-spacing: 0.01em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.item-sub {
  margin-top: 0.15rem;
  font-size: 0.82rem;
  display: flex;
  align-items: baseline;
  gap: 0.35rem;
  font-family: var(--font-mono);
}

.dot {
  opacity: 0.6;
}

.mark {
  color: color-mix(in srgb, var(--fg-0) 86%, transparent);
}

.warning-text {
  color: var(--color-warning);
}

.rarity-common {
  color: var(--color-text);
}

.rarity-uncommon {
  color: color-mix(in srgb, var(--accent-work) 92%, var(--fg-0));
}

.rarity-rare {
  color: color-mix(in srgb, var(--accent-travel) 92%, var(--fg-0));
}

.rarity-epic {
  color: color-mix(in srgb, var(--accent-social) 92%, var(--fg-0));
}

.tooltip-title {
  display: flex;
  gap: 0.6rem;
  align-items: flex-start;
}

.tooltip-icon {
  font-size: 1.4rem;
  line-height: 1;
}

.tooltip-name {
  font-weight: 700;
  font-family: var(--font-display);
  letter-spacing: 0.02em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tooltip-sub {
  font-family: var(--font-mono);
  font-size: 0.8rem;
}
</style>
